/**
 * Replaying the log onto the local copy.
 *
 * An honest limitation, worth understanding before changing anything here: the event log
 * records *what happened*, not the state that resulted. `level_changed` carries the new level
 * and can simply be applied. `opened` does not carry the shortened expiry it caused, and
 * `added` does not carry the item it added, so neither can be replayed into a correct local
 * copy — those events tell this device that something changed and nothing more.
 *
 * So replay applies what it can and asks for a refresh otherwise. That is correct, if less
 * elegant than the log alone being sufficient. The fix, when it is worth making, is to widen
 * the event payloads on the server so they carry the fields they changed.
 */

import type { SyncEvent } from '../api/types';
import type { Database } from '../store/database';
import { forgetGrocery, reviseItem, saveShoppingEntry, shoppingList } from '../store/entities';

export type Replay =
  /** The local copy now matches. */
  | 'applied'
  /** The event names a change this device cannot reconstruct; the server must be re-read. */
  | 'needs-refresh';

export async function replay(db: Database, event: SyncEvent): Promise<Replay> {
  switch (event.kind) {
    case 'level_changed':
      return reviseKnownItem(
        db,
        event.item_id,
        event.to === undefined ? {} : { level: event.to },
      );

    case 'consumed':
    case 'discarded':
      return reviseKnownItem(db, event.item_id, { state: 'gone' });

    // Removal leaves the date on the record, so putting the item back needs nothing the local
    // copy does not already hold.
    case 'restored':
      return reviseKnownItem(db, event.item_id, { state: 'active' });

    // Carries the level and the date it produced, so a restock from another device lands
    // without a re-read. `added_at` is not carried and nothing on this client reads it.
    case 'restocked':
      return reviseKnownItem(db, event.item_id, {
        ...(event.level === undefined ? {} : { level: event.level }),
        state: 'active',
        opened_at: null,
        expires_at: event.expires_at ?? null,
      });

    case 'expiry_overridden':
      return reviseKnownItem(db, event.item_id, {
        expires_at: event.expires_at ?? null,
        expiry_source: 'explicit',
      });

    // The entry is already here; only the fact that it left the list is new.
    case 'shopping_retracted':
      return retractKnownEntry(db, event.entry_id, event.at);

    // Nothing is left to read: the entry goes, and the items it once dated keep their dates.
    case 'grocery_removed':
      return forgetKnownGrocery(db, event.slug);

    // `added` and `shopping_purchased` introduce entities this device has never seen;
    // `opened` and `moved` change an expiry the event does not report; `location_added`,
    // `shopping_added`, `grocery_added` and `grocery_changed` omit the fields that make the
    // entity usable. All of them need the server's copy.
    default:
      return 'needs-refresh';
  }
}

async function forgetKnownGrocery(db: Database, slug: string | undefined): Promise<Replay> {
  if (slug === undefined) {
    return 'needs-refresh';
  }

  await forgetGrocery(db, slug);

  return 'applied';
}

async function retractKnownEntry(
  db: Database,
  entryId: string | undefined,
  at: string,
): Promise<Replay> {
  if (entryId === undefined) {
    return 'needs-refresh';
  }

  const entry = (await shoppingList(db)).find((candidate) => candidate.id === entryId);
  if (entry === undefined) {
    return 'needs-refresh';
  }

  await saveShoppingEntry(db, { ...entry, retracted_at: at });

  return 'applied';
}

async function reviseKnownItem(
  db: Database,
  itemId: string | undefined,
  revision: Parameters<typeof reviseItem>[2],
): Promise<Replay> {
  if (itemId === undefined) {
    return 'needs-refresh';
  }

  const revised = await reviseItem(db, itemId, revision);

  // An item this device has never seen cannot be revised into existence.
  return revised === undefined ? 'needs-refresh' : 'applied';
}
